import { ApolloError } from '@apollo/client';

import { ErrorCode } from '@utils/errorCodes';

import type { GraphQLError } from 'graphql';

/**
 * Extracts the machine-readable `extensions.code` from an ApolloError.
 * Scans every entry in `graphQLErrors` rather than only the first — a single
 * response can carry several errors and the coded one is not always first
 * (see `getFieldErrors` below for the same reasoning). See `ErrorCode`
 * (@utils/errorCodes) for the set of known values — TA-10121.
 */
export function getErrorCode(error?: ApolloError | null): string | undefined {
  return error?.graphQLErrors?.find((e) => e?.extensions?.code)?.extensions
    ?.code as string | undefined;
}

/**
 * Type-safe check for whether an ApolloError carries a specific error code.
 * Prefer this over `error.message.includes(...)` string matching, which
 * breaks silently whenever backend copy changes.
 */
export function isErrorCode(
  error: ApolloError | null | undefined,
  code: ErrorCode
): boolean {
  return getErrorCode(error) === code;
}

/**
 * Returns true if ANY entry in `graphQLErrors` carries the given code.
 * Unlike `isErrorCode` (which uses `getErrorCode`/`find`), this scans all
 * errors — important when a truncation warning accompanies other errors in
 * the same response (e.g. RESULTS_TRUNCATED alongside partial data).
 */
function hasErrorCode(
  error: ApolloError | null | undefined,
  code: ErrorCode
): boolean {
  return (
    error?.graphQLErrors?.some((e) => e?.extensions?.code === code) ?? false
  );
}

/**
 * Accepts either an `ApolloError` (from `useQuery`) or a lazy-query result
 * object (from `useLazyQuery` execute with `errorPolicy: 'all'`, where
 * `error` is undefined and GraphQL errors land in `errors` instead).
 *
 * Checks three locations so both code paths are covered:
 * - `result.graphQLErrors` — direct ApolloError pass (useQuery hooks)
 * - `result.error?.graphQLErrors` — nested error in a query result object
 * - `result.errors` — raw GraphQL error array from lazy execute results
 */
type QueryResultWithErrors = {
  errors?: ReadonlyArray<{
    extensions?: Record<string, unknown> | null;
  }> | null;
  error?: ApolloError | null;
};

export function isResultsTruncated(
  result: ApolloError | QueryResultWithErrors | null | undefined
): boolean {
  if (!result) return false;
  const code = ErrorCode.RESULTS_TRUNCATED;
  if (hasErrorCode(result as ApolloError, code)) return true;
  const { error, errors } = result as QueryResultWithErrors;
  return (
    hasErrorCode(error, code) ||
    (errors?.some((e) => e?.extensions?.code === code) ?? false)
  );
}

/**
 * Extracts field-level validation errors from `extensions.field_errors`
 * (present on VALIDATION_FAILED errors). Returns `{}` when absent so callers
 * can safely iterate without null checks.
 *
 * Merges across every entry in `graphQLErrors`: reading only `[0]` dropped
 * the inline field errors whenever the backend returned another error ahead
 * of the VALIDATION_FAILED one (e.g. `[CONFLICT, VALIDATION_FAILED]`),
 * leaving the form with no per-field feedback at all.
 */
export function getFieldErrors(
  error?: ApolloError | null
): Record<string, string[]> {
  const merged: Record<string, string[]> = {};
  error?.graphQLErrors?.forEach((graphQLError) => {
    const fieldErrors = graphQLError?.extensions?.field_errors as
      | Record<string, string[]>
      | null
      | undefined;
    if (!fieldErrors) return;
    Object.entries(fieldErrors).forEach(([field, messages]) => {
      if (!messages?.length) return;
      merged[field] = [...(merged[field] ?? []), ...messages];
    });
  });
  return merged;
}

/**
 * Extracts the backend-generated `extensions.request_id`, used to correlate
 * a frontend Sentry event with the corresponding backend trace.
 */
export function getRequestId(error?: ApolloError | null): string | undefined {
  return error?.graphQLErrors?.find((e) => e?.extensions?.request_id)
    ?.extensions?.request_id as string | undefined;
}

/**
 * Whether a mutation result represents a failure.
 *
 * `useMutation`'s promise carries failures in two incompatible shapes, so
 * `!result.errors?.length` alone silently treats network failures as success:
 * - GraphQL business errors: the client's `mutate` default `errorPolicy: 'all'`
 *   resolves with `errors` as a `GraphQLError[]`.
 * - Network-level failures (offline, 5xx, Cloudflare edge, timeout): the
 *   promise rejects, and because a call site supplied `onError`, Apollo
 *   swallows the rejection and resolves with `errors` set to a single
 *   `ApolloError` *object* — which has no `.length`
 *   (node_modules/@apollo/client/react/hooks/useMutation.js).
 */
export function mutationFailed(result?: { errors?: unknown } | null): boolean {
  const errors = result?.errors;
  return Array.isArray(errors) ? errors.length > 0 : !!errors;
}

/**
 * Turns the `errors` of a failed `useMutation` result into a throwable
 * `ApolloError`, so a caller that needs the submit promise to reject (rather
 * than resolve into its success-only cleanup) can rethrow it without losing
 * the original error. Handles both shapes `mutationFailed` documents above:
 * an `ApolloError` object for network-level failures, a `GraphQLError[]` for
 * business errors.
 */
export function toMutationError(
  errors: unknown,
  fallbackMessage: string
): ApolloError {
  if (errors instanceof ApolloError) return errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return new ApolloError({ graphQLErrors: errors as GraphQLError[] });
  }
  return new ApolloError({ errorMessage: fallbackMessage });
}

/**
 * Whether the request never reached the GraphQL server: the fetch itself
 * failed (offline, DNS/TLS failure, dropped connection), so there is no HTTP
 * status code and no GraphQL response body. Apollo exposes those as a raw
 * `Failed to fetch` / `Network request failed` message, which form error
 * dialogs used to show to the user verbatim — callers substitute
 * `shared:connectionIssueMsg`, the same copy the circuit-breaker toast uses.
 *
 * Server-side failures (5xx, Cloudflare edge, rate limit) carry a
 * `statusCode` or a GraphQL error body and are deliberately excluded: they
 * already have their own user-facing copy.
 */
export function isConnectionFailure(error?: ApolloError | null): boolean {
  const networkError = error?.networkError;
  // Callers pass errors cast from `unknown`, so networkError is not
  // guaranteed to be an object — `'statusCode' in networkError` would throw
  // on a primitive (some mocks set it to a string) while we are already
  // handling a failure.
  if (!networkError || typeof networkError !== 'object') return false;
  if (error?.graphQLErrors?.length) return false;
  if ('statusCode' in networkError) return false;
  const bodyErrors = (networkError as { result?: { errors?: unknown[] } })
    .result?.errors;
  return !bodyErrors?.length;
}
